import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import useFetch from 'use-http';
import Select, { StylesConfig } from 'react-select';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import PayPeriodOption from 'src/models/personnel/PayPeriodOption';

export type PayHistoryLockStatus = 'Y' | 'N';

// Mirrors every field the Pay_History block's own base-table items expose to Oracle Forms'
// Query menu (Enter Query lets you type a value into any of them before executing) - same idea
// as CheckRegisterSearch's own criteria, just for this screen's block. TOT_HOURS/TE_HOURS aren't
// included - neither is a real column (both are computed live by Post-Query), so neither is
// queryable in the Oracle Form either.
export interface IEmployeePayrollSearchCriteria {
    periodNo: number;
    tsdNo?: number;
    name?: string;
    payxDept?: number;
    payxNo?: number;
    hours?: number;
    otHours?: number;
    pcHours?: number;
    persHours?: number;
    vacHours?: number;
    holHours?: number;
    wSalary?: number;
    prodBonus?: number;
    commission?: number;
    areaCommission?: number;
    extCommission?: number;
    extAreaCommission?: number;
    grossPay?: number;
    expenses?: number;
    payType?: string;
    lockRecord?: PayHistoryLockStatus;
}

interface IEmployeePayrollSearchProps {
    runQuery: (criteria: IEmployeePayrollSearchCriteria) => void;
    // Mirrors :control.period_no on the Oracle screen - available to every toolbar button (Build
    // Payroll, Lock All, etc.) as soon as a period is picked here, independent of whether Search
    // has ever been clicked (Search only re-queries the grid itself). Every option already comes
    // from a validated Pay_Periods lookup, so no separate "does this period exist" check is needed.
    onPeriodSelected?: (periodNo: number | undefined) => void;
}

interface IOptionType {
    value: string;
    label: string;
}

// Matches the plain RHFInputBox `.field` look (src/styles/pages/_leaseInfo.scss:59-65), same as
// CheckRegisterSearch's own Office Select.
const selectStyles: StylesConfig<IOptionType, false> = {
    control: (provided) => ({
        ...provided,
        minHeight: '3rem',
        height: '3rem',
        borderRadius: 0,
        border: 'none',
        borderBottom: '1px solid grey',
        boxShadow: 'none',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '3rem',
        padding: '0px 8px',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '3rem',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0px',
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: '0px',
    }),
};

// Mirrors AP's InvoiceMaintenancePageComponent.getPayPeriods insertCharAt trick - "202614" reads
// as "2026-14".
const formatPeriodLabel = (periodNo: number): string => {
    const str = periodNo.toString();
    const insertIndex = str.length - 2;
    return `${str.slice(0, insertIndex)}-${str.slice(insertIndex)}`;
};

const toPeriodOption = (period: PayPeriodOption): IOptionType => ({
    value: period.periodNo.toString(),
    label: formatPeriodLabel(period.periodNo),
});

// The 7 distinct PAY_TYPE values actually present on PAY_HISTORY_NAME.
const PAY_TYPE_OPTIONS: Array<IOptionType> = [
    { value: 'HOURLY', label: 'HOURLY' },
    { value: 'SALARY', label: 'SALARY' },
    { value: 'PB', label: 'PB' },
    { value: 'CM/PB', label: 'CM/PB' },
    { value: 'AM/SAL', label: 'AM/SAL' },
    { value: 'PM/PC', label: 'PM/PC' },
    { value: 'PC', label: 'PC' },
];

const LOCK_OPTIONS: Array<{ value: PayHistoryLockStatus; label: string }> = [
    { value: 'Y', label: 'Locked' },
    { value: 'N', label: 'Unlocked' },
];

// Mirrors ClientProjectSearch's own Pay Periods Select: options are seeded with a range of
// periods around "now" on mount (see PayHistoryDataAccess.GetPayPeriods), and typing debounces
// into a server-side search (PERIOD_NO contains what was typed) that replaces the options list,
// rather than filtering the already-loaded page client-side.
const EmployeePayrollSearch = (props: IEmployeePayrollSearchProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const { get } = useFetch();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [periodOptions, setPeriodOptions] = useState<Array<IOptionType>>([]);
    const [selectedPeriod, setSelectedPeriod] = useState<IOptionType | undefined>(undefined);
    const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState<boolean>(false);
    const [selectedPayType, setSelectedPayType] = useState<IOptionType | undefined>(undefined);
    const [lockStatus, setLockStatus] = useState<PayHistoryLockStatus | undefined>(undefined);

    const fetchPeriodOptions = async (searchTerm?: string) => {
        const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
        const result = await get(`/v1.0/Payroll/payhistory/periods${query}`);
        if (Array.isArray(result?.items)) {
            setPeriodOptions(result.items.map(toPeriodOption));
        }
    };

    useEffect(() => {
        fetchPeriodOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const periodSearchRef = React.useRef<(inputText: string) => void>();
    periodSearchRef.current = (inputText: string) => fetchPeriodOptions(inputText);
    const [periodSearch] = useState(() => _.debounce((inputText: string) => {
        periodSearchRef.current(inputText);
    }, 500));

    const handlePeriodInputChange = (inputText: string) => {
        if (inputText) {
            periodSearch(inputText.replace(/-/g, ''));
        } else {
            periodSearch.cancel();
            fetchPeriodOptions();
        }
    };

    const toNumber = (numStr: string) => (!!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined);

    const executeSearch = () => {
        const periodNo = Number(selectedPeriod?.value);
        if (!selectedPeriod || isNaN(periodNo)) {
            return;
        }

        const values = getValues();
        const criteria: IEmployeePayrollSearchCriteria = {
            periodNo,
            tsdNo: toNumber(values.tsdNoSearch),
            name: values.nameSearch || undefined,
            payxDept: toNumber(values.payxDeptSearch),
            payxNo: toNumber(values.payxNoSearch),
            hours: toNumber(values.hoursSearch),
            otHours: toNumber(values.otHoursSearch),
            pcHours: toNumber(values.pcHoursSearch),
            persHours: toNumber(values.persHoursSearch),
            vacHours: toNumber(values.vacHoursSearch),
            holHours: toNumber(values.holHoursSearch),
            wSalary: toNumber(values.wSalarySearch),
            prodBonus: toNumber(values.prodBonusSearch),
            commission: toNumber(values.commissionSearch),
            areaCommission: toNumber(values.areaCommissionSearch),
            extCommission: toNumber(values.extCommissionSearch),
            extAreaCommission: toNumber(values.extAreaCommissionSearch),
            grossPay: toNumber(values.grossPaySearch),
            expenses: toNumber(values.expensesSearch),
            payType: selectedPayType?.value,
            lockRecord: lockStatus,
        };

        props.runQuery(criteria);
    };

    const clearSearchFilters = () => {
        setValue('tsdNoSearch', '');
        setValue('nameSearch', '');
        setValue('payxDeptSearch', '');
        setValue('payxNoSearch', '');
        setValue('hoursSearch', '');
        setValue('otHoursSearch', '');
        setValue('pcHoursSearch', '');
        setValue('persHoursSearch', '');
        setValue('vacHoursSearch', '');
        setValue('holHoursSearch', '');
        setValue('wSalarySearch', '');
        setValue('prodBonusSearch', '');
        setValue('commissionSearch', '');
        setValue('areaCommissionSearch', '');
        setValue('extCommissionSearch', '');
        setValue('extAreaCommissionSearch', '');
        setValue('grossPaySearch', '');
        setValue('expensesSearch', '');
        setSelectedPayType(undefined);
        setLockStatus(undefined);
    };

    const numberFieldStyle = { width: '100%' };

    // Reused for every "amount"/"hours" filter row - same narrow width, same right-aligned label.
    const renderNumberField = (label: string, name: string, width = '10%') => (
        <div style={{ width, paddingLeft: '4px' }}>
            <Fieldset label={label} style={{ border: 0, padding: 0 }}>
                <RHFInputBox name={name} type="number" register={register} errors={errors} style={numberFieldStyle} bodyStyle={numberFieldStyle} />
            </Fieldset>
        </div>
    );

    if (isCollapsed) {
        return (
            <div style={{ textAlign: 'center' }}>
                <Button
                    text="(+) Search Filters"
                    className="blue-hover-button"
                    style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                    onClick={() => setIsCollapsed(false)}
                />
            </div>
        );
    }

    return (
        <Page style={{ justifyContent: 'center' }}>
            <Page.Section width={12}>
                <Panel backgroundColor="#D3D3D3">
                    <Panel panelTitle="Employee Payroll Search">
                        <form>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                <div style={{ width: '14%' }}>
                                    <Fieldset label="Period No." style={{ border: 0, padding: 0 }}>
                                        <Select
                                            isClearable={true}
                                            inputId="employeePayrollPeriodSearchInput"
                                            placeholder="Period No."
                                            options={periodOptions}
                                            value={selectedPeriod ?? null}
                                            onChange={(val: IOptionType | null) => {
                                                setSelectedPeriod(val ?? undefined);
                                                props.onPeriodSelected?.(val ? Number(val.value) : undefined);
                                            }}
                                            onInputChange={handlePeriodInputChange}
                                            onMenuOpen={() => setIsPeriodMenuOpen(true)}
                                            onMenuClose={() => setIsPeriodMenuOpen(false)}
                                            menuIsOpen={isPeriodMenuOpen}
                                            isSearchable={true}
                                            styles={selectStyles}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '9%', paddingLeft: '4px' }}>
                                    <Fieldset label="TSDI No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="tsdNoSearch" type="number" register={register} errors={errors} style={numberFieldStyle} bodyStyle={numberFieldStyle} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '16%', paddingLeft: '4px' }}>
                                    <Fieldset label="Employee Name" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="nameSearch" type="text" register={register} errors={errors} style={numberFieldStyle} bodyStyle={numberFieldStyle} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '9%', paddingLeft: '4px' }}>
                                    <Fieldset label="Dept" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="payxDeptSearch" type="number" register={register} errors={errors} style={numberFieldStyle} bodyStyle={numberFieldStyle} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '9%', paddingLeft: '4px' }}>
                                    <Fieldset label="Emp No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="payxNoSearch" type="number" register={register} errors={errors} style={numberFieldStyle} bodyStyle={numberFieldStyle} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '12%', paddingLeft: '4px' }}>
                                    <Fieldset label="Pay Type" style={{ border: 0, padding: 0 }}>
                                        <Select
                                            isClearable={true}
                                            inputId="employeePayrollPayTypeSearchInput"
                                            placeholder="Pay Type"
                                            options={PAY_TYPE_OPTIONS}
                                            value={selectedPayType ?? null}
                                            onChange={(val: IOptionType | null) => setSelectedPayType(val ?? undefined)}
                                            isSearchable={true}
                                            styles={selectStyles}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ display: 'flex', marginLeft: 'auto', flexShrink: 0, paddingLeft: '1rem' }}>
                                    <Button
                                        text="Search"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', borderRadius: '5px' }}
                                        onClick={() => executeSearch()}
                                    />
                                    <Button
                                        text="(-) Minimize"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                        onClick={() => setIsCollapsed(true)}
                                    />
                                    <Button
                                        text="Clear"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                        onClick={() => clearSearchFilters()}
                                    />
                                </div>
                            </div>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ paddingRight: '1rem' }}>
                                    <Fieldset label="Lock?" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', whiteSpace: 'nowrap' }}>
                                        <span style={{ marginLeft: '.5rem' }}>
                                            <input
                                                type="radio"
                                                name="employeePayrollLockSearch"
                                                id="employeePayrollLockSearch-any"
                                                checked={lockStatus === undefined}
                                                onChange={() => setLockStatus(undefined)}
                                            />
                                            <label htmlFor="employeePayrollLockSearch-any" style={{ marginLeft: '.25rem' }}>Any</label>
                                        </span>
                                        {LOCK_OPTIONS.map((option) => (
                                            <span key={option.value} style={{ marginLeft: '.5rem' }}>
                                                <input
                                                    type="radio"
                                                    name="employeePayrollLockSearch"
                                                    id={`employeePayrollLockSearch-${option.value}`}
                                                    checked={lockStatus === option.value}
                                                    onChange={() => setLockStatus(option.value)}
                                                />
                                                <label htmlFor={`employeePayrollLockSearch-${option.value}`} style={{ marginLeft: '.25rem' }}>{option.label}</label>
                                            </span>
                                        ))}
                                    </Fieldset>
                                </div>
                                {renderNumberField('Reg Hours', 'hoursSearch')}
                                {renderNumberField('OT Hours', 'otHoursSearch')}
                                {renderNumberField('PC Hours', 'pcHoursSearch')}
                                {renderNumberField('Pers. Hours', 'persHoursSearch')}
                                {renderNumberField('Vac. Hours', 'vacHoursSearch')}
                                {renderNumberField('Hol. Hours', 'holHoursSearch')}
                            </div>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                {renderNumberField('Salary / Rate', 'wSalarySearch')}
                                {renderNumberField('Production Comp.', 'prodBonusSearch')}
                                {renderNumberField('Performance Bonus', 'commissionSearch')}
                                {renderNumberField('Area Manager Comm.', 'areaCommissionSearch')}
                                {renderNumberField('External Performance Bonus', 'extCommissionSearch', '13%')}
                                {renderNumberField('External Area Manager Comm.', 'extAreaCommissionSearch', '13%')}
                                {renderNumberField('Gross Pay', 'grossPaySearch')}
                                {renderNumberField('Expenses', 'expensesSearch')}
                            </div>
                        </form>
                    </Panel>
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default EmployeePayrollSearch;



// WEBPACK FOOTER //
// ./src/components/personnel/components/EmployeePayrollSearch.tsx